// Annuaire des clubs (D-094), côté appareil.
//  - Pendant la synchro FFBB d'une équipe (ffbb-sync.ts) : l'adversaire de chacune de NOS rencontres est
//    confié à la base, qui rend les fiches à (re)lire (inconnues, ou relues il y a plus d'une semaine) ;
//    l'appareil les lit sur la FFBB par paquets de 40, télécharge les logos nouveaux (déjà réduits par la
//    FFBB), les repasse dans un canvas (128 px, WebP, repli JPEG) et dépose le tout (club_directory_save).
//    La FFBB refuse les serveurs : tout se lit d'ici (voir ffbb.ts).
//  - À l'écran : nom, commune et logo des adversaires (club_directory_cards, gardés le temps de la visite) ;
//    la fiche complète du club adverse d'un match pour le staff (pré-demande de dérogation, D-083).
import { supabase } from './supabase';
import { organismeLogo, organismes } from './ffbb';
import {
  LOGO_SIDE, batches, directoryRow, fitBox, logoAccepted, logoToFetch, trimBox, withLogo, type DirectoryRow, type DueOrg, type OpponentPair,
} from '../domain/annuaire';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}

/** Relie nos rencontres d'un engagement à leur adversaire FFBB ; rend les fiches que la base veut (re)lire. */
export const directoryLink = (teamId: string, engagementId: string, pairs: OpponentPair[]) =>
  rpc<{ linked: number; due: DueOrg[] }>('club_directory_link', { p_team: teamId, p_engagement: engagementId, p_rows: pairs });

export interface SaveResult { saved: number; skipped: number; logos: number; logos_refused: number; prospects: number }
export const directorySave = (teamId: string, rows: DirectoryRow[]) =>
  rpc<SaveResult>('club_directory_save', { p_team: teamId, p_orgs: rows });

/** La fiche du club adverse d'un match (staff du club), et les identifiants FFBB du match. */
export interface OrgContact {
  ffbb_match_id: string | null; ffbb_engagement_id: string | null; org_id: string | null; known: boolean;
  name: string | null; code: string | null; town: string | null; email: string | null; phone: string | null;
  website: string | null; logo: string | null; read_at: string | null;
}
export const directoryContact = (eventId: string) => rpc<OrgContact | null>('club_directory_contact', { p_event: eventId });

/** Une image réduite dans un canvas : marges retirées, 128 px au plus, WebP (repli JPEG sur fond blanc :
 *  Safari n'écrit pas le WebP), sous la limite de la base. Null si l'image est illisible ou reste trop lourde. */
export async function reduceLogo(blob: Blob): Promise<string | null> {
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(blob); } catch { return null; }
  try {
    // Le cadre utile, lu sur une copie de 256 px au plus.
    const s0 = fitBox(bitmap.width, bitmap.height, LOGO_SIDE * 2);
    const src = document.createElement('canvas');
    src.width = s0.w; src.height = s0.h;
    const g0 = src.getContext('2d', { willReadFrequently: true });
    if (!g0) return null;
    g0.drawImage(bitmap, 0, 0, s0.w, s0.h);
    const box = trimBox(g0.getImageData(0, 0, s0.w, s0.h).data, s0.w, s0.h);
    for (const side of [LOGO_SIDE, 96, 64]) {
      const { w, h } = fitBox(box.w, box.h, side);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d');
      if (!g) return null;
      g.drawImage(src, box.x, box.y, box.w, box.h, 0, 0, w, h);
      const webp = c.toDataURL('image/webp', 0.82);
      if (webp.startsWith('data:image/webp') && logoAccepted(webp)) return webp;
      g.globalCompositeOperation = 'destination-over';
      g.fillStyle = '#FFFFFF';
      g.fillRect(0, 0, w, h);
      for (const q of [0.85, 0.65]) {
        const jpeg = c.toDataURL('image/jpeg', q);
        if (logoAccepted(jpeg)) return jpeg;
      }
    }
    return null;
  } finally { bitmap.close(); }
}

const fetchLogo = async (ref: string): Promise<string | null> => {
  try {
    const b = await organismeLogo(ref);
    return b ? await reduceLogo(b) : null;
  } catch { return null; }
};

/** Les fiches demandées par la base : lues sur la FFBB par paquets, logos nouveaux réduits, déposées.
 *  Rend le nombre de fiches gardées. Une panne arrête la lecture (la base les redemandera). */
export async function readDirectory(teamId: string, due: DueOrg[]): Promise<number> {
  const byId = new Map(due.map(d => [d.org_id, d]));
  let saved = 0;
  for (const part of batches([...byId.keys()])) {
    const rows = (await organismes(part)).map(directoryRow).filter((r): r is DirectoryRow => r !== null && byId.has(r.org_id));
    const out: DirectoryRow[] = [];
    for (const r of rows) {
      const d = byId.get(r.org_id);
      out.push(withLogo(r, d, logoToFetch(r, d) ? await fetchLogo(r.logo_ref!) : null));
    }
    if (!out.length) continue;
    saved += (await directorySave(teamId, out)).saved;
    forgetCards(out.map(r => r.org_id));
  }
  return saved;
}

/** Repli de la pré-demande de dérogation (D-083) : la fiche de l'adversaire lue sur la FFBB pour un match
 *  met l'annuaire à jour (le match apprend son adversaire, logo compris). `hasLogo` : l'annuaire en garde
 *  déjà un (pas de nouveau téléchargement). */
export async function refreshFromMatch(teamId: string, engagementId: string, ffbbMatchId: string, org: Record<string, any>, hasLogo = false): Promise<void> {
  const row = directoryRow(org);
  if (!row) return;
  const link = await directoryLink(teamId, engagementId, [{ ffbb_id: ffbbMatchId, org_id: row.org_id }]);
  const d = link.due.find(x => x.org_id === row.org_id) ?? (hasLogo ? { org_id: row.org_id, logo_ref: row.logo_ref, has_logo: true } : null);
  await directorySave(teamId, [withLogo(row, d, logoToFetch(row, d) ? await fetchLogo(row.logo_ref!) : null)]);
  forgetCards([row.org_id]);
}

// --- À l'écran : nom, commune et logo des adversaires --------------------------------------------------------
export interface OrgCard { org_id: string; name: string; town: string | null; logo: string | null }
const cards = new Map<string, Promise<OrgCard | null>>();

/** Nom, commune et logo de ces clubs adverses (ceux que le compte peut voir), gardés le temps de la visite ;
 *  une seule requête pour tous ceux qui manquent. */
export async function orgCards(ids: (string | null | undefined)[]): Promise<Map<string, OrgCard>> {
  const want = [...new Set(ids.filter((x): x is string => !!x && /^[0-9]{1,20}$/.test(x)))];
  for (const part of batches(want.filter(id => !cards.has(id)), 100)) {
    const ask = rpc<OrgCard[]>('club_directory_cards', { p_orgs: part });
    for (const id of part) {
      cards.set(id, ask.then(list => list.find(c => c.org_id === id) ?? null, () => { cards.delete(id); return null; }));
    }
  }
  const got = await Promise.all(want.map(id => cards.get(id)!));
  return new Map(got.filter((c): c is OrgCard => !!c).map(c => [c.org_id, c]));
}
/** Un logo changé (fiche relue) : relu à la prochaine demande. */
export const forgetCards = (ids: string[]) => ids.forEach(id => cards.delete(id));
